import clsx from 'clsx';
import {
  Col,
  Container,
  getTypographyDefaultColor,
  RatingStars,
  Row,
  Space,
  Typography
} from '@ag1/ui';
import { HeroSlim, SanityKeyed } from 'types/studio/studio';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import CustomPortableText from '../PortableText/PortableText';
import styles from './HeroSlim.module.scss';
import { StudioHotspotImageV2 } from '../StudioHotspotImage';
import { CommonSectionProps } from '../types';

export const HeroSlimBlockMarkup = ({
  _key: key,
  superHeadline,
  headline,
  smallerTextSpan,
  addImageMobile,
  image,
  text,
  disclaimer,
  theme,
  padding,
  starsCount,
  reviewCount,
  reviewText
}: SanityKeyed<HeroSlim> & CommonSectionProps) => (
  <>
    {image?.asset && addImageMobile && (
      <div className={styles.imageContainer}>
        <StudioHotspotImageV2 image={image} className={styles.image} eagerLoading />
      </div>
    )}
    <Container narrow id={key}>
      <Space
        top={{ xxs: 40, s: 40, m: 56, l: 60 }}
        bottom={{ xxs: 24, s: 24, m: 56, l: 60 }}
        className={clsx(getSectionPaddingClasses(styles, padding))}>
        <Row>
          <Col span={{ xxs: 12, l: 11 }}>
            {starsCount && (reviewCount || reviewText) ? (
              <aside className={clsx(styles.ratingBlock)}>
                <RatingStars
                  rating={starsCount}
                  thin
                  theme={theme}
                  className={styles.ratingStars}
                  inheritFontSize
                />
                <Typography
                  component="div"
                  variant="portalCaption"
                  size="2"
                  noPadding
                  color={getTypographyDefaultColor(theme)}
                  className={styles.ratingText}
                  weight="400">
                  {!reviewText && reviewCount
                    ? `${reviewCount.toLocaleString()} Verified ${starsCount}-star reviews`
                    : reviewText}
                </Typography>
              </aside>
            ) : null}
            {superHeadline && (
              <Typography
                component="p"
                variant="captionMono"
                size="2"
                noPadding
                className={styles.superHeadline}>
                {superHeadline}
              </Typography>
            )}
            <Typography
              variant="headline"
              component="h1"
              size="1"
              noPadding
              className={clsx(smallerTextSpan && styles.smallerTextSpan, styles.title)}
              color={getTypographyDefaultColor(theme)}>
              {headline}
            </Typography>
            {text && (
              <Space top={{ xxs: 8, s: 16 }}>
                <Typography
                  component="div"
                  variant="body"
                  size="1"
                  noPadding
                  className={styles.text}
                  color={getTypographyDefaultColor(theme)}>
                  <CustomPortableText addAccessibility value={text} />
                </Typography>
              </Space>
            )}
            {disclaimer && (
              <Typography
                className={styles.disclaimer}
                component="div"
                variant="subtitle"
                size="1"
                fontStyle="italic"
                noPadding
                color="grey-1-new">
                {disclaimer}
              </Typography>
            )}
          </Col>
        </Row>
      </Space>
    </Container>
  </>
);
